<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- 헤더 -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">관리자 대시보드</h1>
      <p class="mt-2 text-gray-600">시스템 전반의 통계와 현황을 확인하세요</p>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner w-8 h-8"></div>
    </div>

    <!-- 대시보드 내용 -->
    <div v-else class="space-y-8">
      <!-- 주요 통계 카드 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- 전체 사용자 -->
        <div class="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-blue-100 text-sm font-medium">전체 사용자</p>
              <p class="text-3xl font-bold">{{ stats.users?.total || 0 }}</p>
              <p class="text-blue-100 text-sm mt-1">
                이번 주 +{{ stats.users?.newThisWeek || 0 }}명
              </p>
            </div>
            <UsersIcon class="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <!-- 활성 게시글 -->
        <div class="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-green-100 text-sm font-medium">활성 게시글</p>
              <p class="text-3xl font-bold">{{ stats.posts?.active || 0 }}</p>
              <p class="text-green-100 text-sm mt-1">
                전체 {{ stats.posts?.total || 0 }}개 중
              </p>
            </div>
            <DocumentTextIcon class="w-12 h-12 text-green-200" />
          </div>
        </div>

        <!-- 활성 예약 -->
        <div class="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-purple-100 text-sm font-medium">활성 예약</p>
              <p class="text-3xl font-bold">{{ stats.reservations?.active || 0 }}</p>
              <p class="text-purple-100 text-sm mt-1">
                전체 {{ stats.reservations?.total || 0 }}개 중
              </p>
            </div>
            <CalendarIcon class="w-12 h-12 text-purple-200" />
          </div>
        </div>

        <!-- 관리자 수 -->
        <div class="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-6 shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-orange-100 text-sm font-medium">관리자</p>
              <p class="text-3xl font-bold">{{ stats.users?.admin || 0 }}</p>
              <p class="text-orange-100 text-sm mt-1">
                일반 {{ stats.users?.regular || 0 }}명
              </p>
            </div>
            <ShieldCheckIcon class="w-12 h-12 text-orange-200" />
          </div>
        </div>
      </div>

      <!-- 콘텐츠 통계 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div class="bg-white rounded-xl shadow-sm border p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">콘텐츠 현황</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <BellIcon class="w-5 h-5 text-blue-500 mr-3" />
                <span class="text-gray-700">공지사항</span>
              </div>
              <span class="text-2xl font-bold text-gray-900">{{ stats.content?.notices || 0 }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <QuestionMarkCircleIcon class="w-5 h-5 text-green-500 mr-3" />
                <span class="text-gray-700">Q&A</span>
              </div>
              <span class="text-2xl font-bold text-gray-900">{{ stats.content?.qnas || 0 }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <DocumentTextIcon class="w-5 h-5 text-purple-500 mr-3" />
                <span class="text-gray-700">이번 주 새 게시글</span>
              </div>
              <span class="text-2xl font-bold text-gray-900">{{ stats.posts?.newThisWeek || 0 }}</span>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm border p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">시스템 정보</h3>
          <div v-if="systemInfo" class="space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-gray-700">JVM 업타임</span>
              <span class="text-gray-900 font-medium">{{ systemInfo.jvm?.uptime || 0 }}분</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700">메모리 사용률</span>
              <span class="text-gray-900 font-medium">
                {{ systemInfo.jvm?.usedMemory || 0 }}MB / {{ systemInfo.jvm?.totalMemory || 0 }}MB
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700">CPU 코어</span>
              <span class="text-gray-900 font-medium">{{ systemInfo.system?.cores || 0 }}개</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700">데이터베이스</span>
              <span class="flex items-center">
                <div 
                  class="w-2 h-2 rounded-full mr-2"
                  :class="systemInfo.database?.status === 'connected' ? 'bg-green-500' : 'bg-red-500'"
                ></div>
                <span class="text-gray-900 font-medium capitalize">
                  {{ systemInfo.database?.status || 'Unknown' }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 빠른 액션 -->
      <div class="bg-white rounded-xl shadow-sm border p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">빠른 관리</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <RouterLink
            to="/admin/users"
            class="flex items-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors group"
          >
            <UsersIcon class="w-8 h-8 text-blue-600 mr-3 group-hover:text-blue-700" />
            <div>
              <p class="font-medium text-blue-900">사용자 관리</p>
              <p class="text-sm text-blue-600">권한 변경, 계정 관리</p>
            </div>
          </RouterLink>

          <RouterLink
            to="/notices/create"
            class="flex items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors group"
          >
            <BellIcon class="w-8 h-8 text-green-600 mr-3 group-hover:text-green-700" />
            <div>
              <p class="font-medium text-green-900">공지사항 작성</p>
              <p class="text-sm text-green-600">새 공지사항 등록</p>
            </div>
          </RouterLink>

          <RouterLink
            to="/posts"
            class="flex items-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors group"
          >
            <DocumentTextIcon class="w-8 h-8 text-purple-600 mr-3 group-hover:text-purple-700" />
            <div>
              <p class="font-medium text-purple-900">게시글 관리</p>
              <p class="text-sm text-purple-600">게시글 모니터링</p>
            </div>
          </RouterLink>

          <button
            @click="refreshData"
            :disabled="loading"
            class="flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors group"
            :class="{ 'opacity-50 cursor-not-allowed': loading }"
          >
            <ArrowPathIcon class="w-8 h-8 text-gray-600 mr-3 group-hover:text-gray-700" />
            <div>
              <p class="font-medium text-gray-900">새로고침</p>
              <p class="text-sm text-gray-600">최신 데이터 갱신</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import {
  UsersIcon,
  DocumentTextIcon,
  CalendarIcon,
  ShieldCheckIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon
} from '@heroicons/vue/24/outline'
import { adminApi } from '@/services/api'
import { useToast } from 'vue-toastification'

const toast = useToast()

const loading = ref(false)
const stats = ref<any>({})
const systemInfo = ref<any>(null)

const loadDashboardData = async () => {
  try {
    loading.value = true
    
    // 대시보드 통계와 시스템 정보를 병렬로 로드
    const [statsResponse, systemResponse] = await Promise.all([
      adminApi.getDashboardStats(),
      adminApi.getSystemInfo()
    ])
    console.log(statsResponse)
    stats.value = statsResponse.data.data
    systemInfo.value = systemResponse.data.data
  } catch (error: any) {
    console.error('Failed to load dashboard data:', error)
    toast.error('대시보드 데이터를 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  loadDashboardData()
}

onMounted(() => {
  loadDashboardData()
})
</script>

<style scoped>
.spinner {
  border: 2px solid #f3f4f6;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
